import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, Button, Alert, Paper, Select, MenuItem,
  FormControl, Chip, Tooltip, CircularProgress, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, alpha, useTheme, Collapse, Pagination, Drawer, IconButton, Stack
} from '@mui/material';
import {
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import axios from '../utils/axios';

/* ── Inline SVG Icons ─────────────────────────────────────────────── */
const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 10a7 7 0 0112.9-3.7M17 10a7 7 0 01-12.9 3.7" strokeLinecap="round" />
    <path d="M16 3v4h-4M4 17v-4h4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="9" cy="9" r="6" /><path d="M13.5 13.5L17 17" strokeLinecap="round" />
  </svg>
);
const ChevronIcon = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
    <path d="M7 4l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ServerIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="14" height="5" rx="1.5" /><rect x="3" y="12" width="14" height="5" rx="1.5" /><circle cx="6" cy="5.5" r="0.75" fill="currentColor" /><circle cx="6" cy="14.5" r="0.75" fill="currentColor" /></svg>);
const DatabaseIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><ellipse cx="10" cy="5" rx="7" ry="3" /><path d="M3 5v10c0 1.66 3.13 3 7 3s7-1.34 7-3V5" /><path d="M3 10c0 1.66 3.13 3 7 3s7-1.34 7-3" /></svg>);
const BucketIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4h12l-1.5 13H5.5L4 4z" strokeLinejoin="round" /><path d="M3 4h14" strokeLinecap="round" /></svg>);
const LambdaIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 17L8 3h2l3 8.5L17 3" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const DiskIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="10" cy="10" r="7" /><circle cx="10" cy="10" r="2" /></svg>);
const LoadBalancerIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="10" cy="4" r="2" /><circle cx="5" cy="16" r="2" /><circle cx="15" cy="16" r="2" /><path d="M10 6v4M8 12l-3 2M12 12l3 2" strokeLinecap="round" /></svg>);
const ShieldIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10 2L3 5v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V5l-7-3z" strokeLinejoin="round" /></svg>);
const CloudIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5.5 16h9a4 4 0 001-7.87A5.5 5.5 0 004 9.5 3.5 3.5 0 005.5 16z" /></svg>);
const NetworkIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="10" cy="10" r="7" /><path d="M3 10h14M10 3c2 2.5 3 5 3 7s-1 4.5-3 7c-2-2.5-3-5-3-7s1-4.5 3-7z" /></svg>);
const ContainerIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="4" width="16" height="12" rx="2" /><path d="M2 8h16M6 4v12M10 4v12M14 4v12" /></svg>);
const KeyIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="4" /><path d="M11 11l6 6M14 14l2-2" strokeLinecap="round" /></svg>);
const MailIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="4" width="16" height="12" rx="2" /><path d="M2 4l8 6 8-6" /></svg>);
const CogIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="10" cy="10" r="3" /><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.2 4.2l1.4 1.4M14.4 14.4l1.4 1.4M4.2 15.8l1.4-1.4M14.4 5.6l1.4-1.4" strokeLinecap="round" /></svg>);
const ApiIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 7l-2 3 2 3M16 7l2 3-2 3M12 4l-4 12" strokeLinecap="round" strokeLinejoin="round" /></svg>);
const QueueIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="14" height="4" rx="1" /><rect x="3" y="9" width="14" height="4" rx="1" /><rect x="3" y="15" width="8" height="2" rx="1" /></svg>);
const BoxIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="14" height="14" rx="2" /><path d="M3 8h14M8 3v14" /></svg>);
const NatIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="7" width="14" height="6" rx="1.5" /><path d="M7 7V5M13 7V5M7 13v2M13 13v2" strokeLinecap="round" /></svg>);
const SnapshotIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="14" height="14" rx="2" /><circle cx="10" cy="10" r="3" /><path d="M14 3l-4 4" strokeLinecap="round" /></svg>);
const IpIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="10" cy="10" r="7" /><path d="M3 10h14M10 3c2 2.5 3 5 3 7s-1 4.5-3 7c-2-2.5-3-5-3-7s1-4.5 3-7z" /></svg>);
const LogIcon = () => (<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="2" width="12" height="16" rx="2" /><path d="M7 6h6M7 9h6M7 12h4" strokeLinecap="round" /></svg>);

function getServiceIcon(serviceName) {
  const s = serviceName.toLowerCase();
  if (s.includes('elastic compute') || s.includes('ec2')) return <ServerIcon />;
  if (s.includes('lightsail')) return <ServerIcon />;
  if (s.includes('relational database') || s.includes('rds')) return <DatabaseIcon />;
  if (s.includes('dynamodb') || s.includes('redshift') || s.includes('elasticache') || s.includes('neptune') || s.includes('documentdb')) return <DatabaseIcon />;
  if (s.includes('simple storage') || s.includes('s3') || s.includes('glacier')) return <BucketIcon />;
  if (s.includes('elastic block') || s.includes('ebs') || s.includes('elastic file') || s.includes('efs')) return <DiskIcon />;
  if (s.includes('backup')) return <SnapshotIcon />;
  if (s.includes('lambda') || s.includes('step function') || s.includes('states')) return <LambdaIcon />;
  if (s.includes('elastic load') || s.includes('load balanc') || s.includes('elb')) return <LoadBalancerIcon />;
  if (s.includes('vpc') || s.includes('virtual private') || s.includes('direct connect') || s.includes('transit gateway')) return <NetworkIcon />;
  if (s.includes('route 53') || s.includes('route53') || s.includes('elastic ip') || s.includes('global accelerator')) return <IpIcon />;
  if (s.includes('cloudfront')) return <CloudIcon />;
  if (s.includes('nat gateway')) return <NatIcon />;
  if (s.includes('api gateway')) return <ApiIcon />;
  if (s.includes('key management') || s.includes('kms') || s.includes('secrets manager')) return <KeyIcon />;
  if (s.includes('shield') || s.includes('waf') || s.includes('firewall') || s.includes('guardduty') || s.includes('inspector') || s.includes('macie') || s.includes('certificate') || s.includes('acm') || s.includes('iam') || s.includes('identity') || s.includes('cognito')) return <ShieldIcon />;
  if (s.includes('container') || s.includes('ecs') || s.includes('eks') || s.includes('fargate') || s.includes('ecr')) return <ContainerIcon />;
  if (s.includes('simple notification') || s.includes('sns') || s.includes('simple email') || s.includes('ses') || s.includes('pinpoint')) return <MailIcon />;
  if (s.includes('simple queue') || s.includes('sqs') || s.includes('eventbridge') || s.includes('event')) return <QueueIcon />;
  if (s.includes('cloudwatch') || s.includes('cloudtrail')) return <LogIcon />;
  if (s.includes('config') || s.includes('systems manager') || s.includes('ssm') || s.includes('cloudformation')) return <CogIcon />;
  if (s.includes('bedrock') || s.includes('sagemaker')) return <LambdaIcon />;
  if (s.includes('cloud')) return <CloudIcon />;
  if (s.includes('tax') || s.includes('support') || s.includes('marketplace')) return <BoxIcon />;
  return <BoxIcon />;
}

const CHART_COLORS = ['#6366f1','#8b5cf6','#a78bfa','#c4b5fd','#818cf8','#4f46e5','#7c3aed','#5b21b6','#4338ca','#3730a3','#6d28d9','#9333ea'];
const TH = { fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, py: 1 };
const TD = { fontSize: '0.8rem' };
const MONO = { fontSize: '0.75rem', fontFamily: 'monospace' };
const PAGE_SIZE = 50;

function fmt(amount) {
  const val = amount || 0;
  if (val !== 0 && Math.abs(val) < 0.01) return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(val);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
}

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', boxShadow: 2 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>{label || payload[0]?.name}</Typography>
      {payload.map((p, i) => (
        <Typography key={i} sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{p.name || 'Cost'}: {fmt(p.value)}</Typography>
      ))}
    </Paper>
  );
};


/* ── Chart Sub-Components ─────────────────────────────────────────── */
function ServicePieChart({ services }) {
  const data = (services || []).slice(0, 8).map((s, i) => ({
    name: s.service.replace(/Amazon |AWS /g, '').substring(0, 25),
    value: Math.abs(s.totalCost)
  }));
  if (data.length === 0) return null;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '0.6rem' }}>
          {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Pie>
        <RTooltip content={<ChartTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function DailyTrendChart({ dailyCosts }) {
  if (!dailyCosts?.length) return null;
  const data = dailyCosts.map(d => ({ date: d.date.substring(5), cost: d.cost }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(data.length / 8))} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v.toFixed(0)}`} />
        <RTooltip content={<ChartTooltip />} />
        <Line type="monotone" dataKey="cost" stroke="#6366f1" strokeWidth={2} dot={false} name="Daily Cost" />
      </LineChart>
    </ResponsiveContainer>
  );
}

function TopResourcesChart({ topResources }) {
  if (!topResources?.length) return null;
  const data = topResources.slice(0, 10).map(r => ({
    name: (r.name || r.resourceId || '').substring(0, 20),
    cost: r.cost
  }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${v.toFixed(0)}`} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={120} />
        <RTooltip content={<ChartTooltip />} />
        <Bar dataKey="cost" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Cost" />
      </BarChart>
    </ResponsiveContainer>
  );
}

function RegionDistributionChart({ regionDistribution }) {
  if (!regionDistribution?.length) return null;
  const data = regionDistribution.slice(0, 8).map(r => ({
    name: r.region || 'global',
    cost: Math.abs(r.cost)
  }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="name" tick={{ fontSize: 9 }} />
        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v.toFixed(0)}`} />
        <RTooltip content={<ChartTooltip />} />
        <Bar dataKey="cost" fill="#a78bfa" radius={[4, 4, 0, 0]} name="Cost" />
      </BarChart>
    </ResponsiveContainer>
  );
}


/* ── Error State Components ───────────────────────────────────────── */
function ErrorState({ error }) {
  if (!error) return null;
  const errBody = typeof error === 'object' ? error : { error: 'UNKNOWN', message: String(error), setupInstructions: [] };

  if (errBody.error === 'PERMISSION_DENIED') {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>Missing IAM Permissions</Typography>
        <Typography sx={{ fontSize: '0.8rem', mb: 1 }}>{errBody.message}</Typography>
        {errBody.setupInstructions?.map((inst, i) => (
          <Typography key={i} sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>{inst}</Typography>
        ))}
      </Alert>
    );
  }
  if (errBody.error === 'NOT_ENABLED') {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>Cost Explorer Not Enabled</Typography>
        <Typography sx={{ fontSize: '0.8rem', mb: 1 }}>{errBody.message}</Typography>
        {errBody.setupInstructions?.map((inst, i) => (
          <Typography key={i} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{inst}</Typography>
        ))}
      </Alert>
    );
  }
  if (errBody.error === 'THROTTLED') {
    return <Alert severity="info" sx={{ borderRadius: 2 }}>API rate limit reached. Please try again in a few minutes.</Alert>;
  }
  return <Alert severity="warning" sx={{ borderRadius: 2 }}>{errBody.message || 'An error occurred loading cost data.'}</Alert>;
}

/* ── Service Drill-Down Row ───────────────────────────────────────── */
function ServiceDrillDown({ accountId, service, timeRange, customStartDate, customEndDate, theme }) {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [resourcePage, setResourcePage] = useState(1);

  useEffect(() => {
    loadDetails();
  }, []);

  const loadDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ service });
      if (timeRange === 'custom') {
        if (customStartDate) params.append('startDate', customStartDate);
        if (customEndDate) params.append('endDate', customEndDate);
      } else {
        params.append('timeRange', timeRange);
      }
      const res = await axios.get(`/api/cost/resource-breakdown/${accountId}/service-details?${params}`);
      setDetails(res.data);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally { setLoading(false); }
  };

  if (loading) return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /><Typography variant="caption" sx={{ ml: 1 }}>Loading details...</Typography></Box>;
  if (error) return <Box sx={{ p: 2 }}><ErrorState error={error} /></Box>;
  if (!details) return null;

  const resources = details.resources || [];
  const totalPages = Math.ceil(resources.length / PAGE_SIZE);
  const pagedResources = resources.slice((resourcePage - 1) * PAGE_SIZE, resourcePage * PAGE_SIZE);

  return (
    <Box>
      {/* Component breakdown */}
      {details.components?.length > 0 && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 1 }}>Usage Type Breakdown</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, pl: 1 }}>Usage Type</TableCell>
                <TableCell align="right" sx={TH}>Cost</TableCell>
                <TableCell align="right" sx={TH}>Usage</TableCell>
                <TableCell align="right" sx={TH}>%</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {details.components.slice(0, 20).map((c, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ ...TD, pl: 1, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={c.usageType}><span>{c.usageType}</span></Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ ...TD, fontWeight: 600 }}>{fmt(c.cost || c.totalCost)}</TableCell>
                  <TableCell align="right" sx={{ ...TD, color: 'text.secondary' }}>{c.usage > 0 ? c.usage.toLocaleString() : '-'}</TableCell>
                  <TableCell align="right" sx={{ ...TD, color: 'text.secondary' }}>{c.percentage?.toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Resource table */}
      {resources.length > 0 && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 1 }}>
            Resources ({resources.length})
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, pl: 1 }}>Resource</TableCell>
                <TableCell sx={TH}>Resource ID</TableCell>
                <TableCell sx={TH}>Type</TableCell>
                <TableCell sx={TH}>State</TableCell>
                <TableCell sx={TH}>Region</TableCell>
                <TableCell sx={TH}>Tags</TableCell>
                <TableCell align="right" sx={TH}>Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedResources.map((r, ri) => {
                const tagEntries = Object.entries(r.tags || {});
                return (
                  <TableRow key={ri} hover>
                    <TableCell sx={{ ...TD, pl: 1, fontWeight: 500, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={r.resourceName || r.resourceId}><span>{r.resourceName || r.resourceId}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...MONO, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={r.resourceId}><span>{r.resourceId}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...TD, color: 'text.secondary' }}>{r.instanceType || r.resourceType || '-'}</TableCell>
                    <TableCell>
                      {r.state ? (
                        <Chip size="small" label={r.state} variant="outlined"
                          color={['running','active','available','in-use'].includes(r.state) ? 'success' : ['stopped','terminated'].includes(r.state) ? 'error' : 'default'}
                          sx={{ height: 22, fontSize: '0.68rem', fontWeight: 500 }} />
                      ) : <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>-</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{r.region || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      {tagEntries.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {tagEntries.slice(0, 2).map(([k, v], ti) => (
                            <Tooltip key={ti} title={`${k}: ${v}`}>
                              <Chip size="small" variant="outlined" label={`${k}: ${v.length > 10 ? v.substring(0, 8) + '..' : v}`} sx={{ fontSize: '0.6rem', height: 18 }} />
                            </Tooltip>
                          ))}
                          {tagEntries.length > 2 && <Chip size="small" variant="outlined" label={`+${tagEntries.length - 2}`} sx={{ fontSize: '0.6rem', height: 18 }} />}
                        </Box>
                      ) : <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>-</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ ...TD, fontWeight: 600, color: r.cost < 0 ? '#10B981' : 'text.primary' }}>
                      {r.cost !== null && r.cost !== undefined ? fmt(r.cost) : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
              <Pagination count={totalPages} page={resourcePage} onChange={(_, p) => setResourcePage(p)} size="small" />
            </Box>
          )}
        </Box>
      )}

      {resources.length === 0 && details.components?.length > 0 && (
        <Box sx={{ px: 2, py: 2 }}>
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
            Individual resource IDs are not available for this service. The usage type breakdown above shows the cost components.
          </Alert>
        </Box>
      )}
    </Box>
  );
}


/* ── Main Component ───────────────────────────────────────────────── */
function ResourceBreakdown() {
  const theme = useTheme();
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [timeRange, setTimeRange] = useState('30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');

  // Astra chatbot state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatServiceContext, setChatServiceContext] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => { loadAccounts(); }, []);
  useEffect(() => {
    if (selectedAccount) {
      if (timeRange === 'custom') {
        if (customStartDate && customEndDate) loadData();
      } else {
        loadData();
      }
    }
  }, [selectedAccount, timeRange, customStartDate, customEndDate]);

  const loadAccounts = async () => {
    try {
      const res = await axios.get('/api/aws/accounts');
      const accounts = res.data.accounts || [];
      setAwsAccounts(accounts);
      if (accounts.length > 0) setSelectedAccount(accounts[0].id);
    } catch (err) { console.error('Failed to load AWS accounts:', err); }
  };

  const loadData = async (forceRefresh = false) => {
    if (!selectedAccount) return;
    setLoading(true);
    setError(null);
    setData(null);
    setExpanded({});
    try {
      const params = new URLSearchParams();
      if (timeRange === 'custom') {
        params.append('startDate', customStartDate);
        params.append('endDate', customEndDate);
      } else {
        params.append('timeRange', timeRange);
      }
      if (forceRefresh) params.append('forceRefresh', 'true');
      const res = await axios.get(`/api/cost/resource-breakdown/${selectedAccount}?${params}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data || err.message);
    } finally { setLoading(false); }
  };

  const toggleExpand = (svc) => setExpanded(prev => ({ ...prev, [svc]: !prev[svc] }));

  // Astra chat handlers
  const handleChatSend = async () => {
    if (!chatInput.trim() || !selectedAccount || isChatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsChatLoading(true);
    try {
      const history = chatMessages.map(m => ({ role: m.role, content: m.content }));
      const resourceContext = data ? {
        grandTotal: data.grandTotal,
        serviceCount: data.serviceCount,
        period: data.period,
        services: data.services?.slice(0, 15),
        expandedService: chatServiceContext
      } : null;
      const res = await axios.post(`/api/cost/resource-breakdown-chat/${selectedAccount}`, {
        message: userMsg,
        conversationHistory: history,
        resourceContext
      });
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: err.response?.data?.error || 'Something went wrong. Please try again.' }]);
    } finally { setIsChatLoading(false); }
  };

  const openChatForService = (serviceName, details) => {
    setChatServiceContext({ service: serviceName, components: details?.components, resources: details?.resources?.slice(0, 10) });
    setChatMessages([]);
    setChatInput('');
    setChatOpen(true);
  };

  const openChatGlobal = () => {
    setChatServiceContext(null);
    setChatMessages([]);
    setChatInput('');
    setChatOpen(true);
  };

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isChatLoading]);

  const filtered = useMemo(() => {
    if (!data?.services) return [];
    if (!search.trim()) return data.services;
    const q = search.toLowerCase();
    return data.services.filter(s => s.service.toLowerCase().includes(q));
  }, [data, search]);

  // Date validation for custom range
  const dateError = useMemo(() => {
    if (timeRange !== 'custom') return null;
    if (!customStartDate || !customEndDate) return null;
    if (new Date(customStartDate) >= new Date(customEndDate)) return 'Start date must be before end date';
    if (new Date(customEndDate) > new Date()) return 'End date cannot be in the future';
    return null;
  }, [timeRange, customStartDate, customEndDate]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.3rem' }}>Cost Per Resource</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
            AWS cost insights with service drill-down and visual analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {awsAccounts.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value={selectedAccount} onChange={e => setSelectedAccount(e.target.value)} sx={{ fontSize: '0.8rem', borderRadius: 2 }}>
                {awsAccounts.map(a => <MenuItem key={a.id} value={a.id} sx={{ fontSize: '0.8rem' }}>{a.account_name || a.account_id}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select value={timeRange} onChange={e => setTimeRange(e.target.value)} sx={{ fontSize: '0.8rem', borderRadius: 2 }}>
              <MenuItem value="1">Today</MenuItem>
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 3 months</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
          {timeRange === 'custom' && (
            <>
              <TextField size="small" type="date" label="Start" value={customStartDate}
                onChange={e => setCustomStartDate(e.target.value)} InputLabelProps={{ shrink: true }}
                inputProps={{ max: customEndDate || new Date().toISOString().split('T')[0] }}
                sx={{ width: 145, '& .MuiInputBase-root': { fontSize: '0.8rem', borderRadius: 2 } }} />
              <TextField size="small" type="date" label="End" value={customEndDate}
                onChange={e => setCustomEndDate(e.target.value)} InputLabelProps={{ shrink: true }}
                inputProps={{ min: customStartDate, max: new Date().toISOString().split('T')[0] }}
                sx={{ width: 145, '& .MuiInputBase-root': { fontSize: '0.8rem', borderRadius: 2 } }} />
            </>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadData(true)} disabled={loading}
            sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.8rem' }}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {dateError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{dateError}</Alert>}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading cost data...</Typography>
          </Box>
        </Box>
      )}

      {/* Error */}
      {error && !loading && <ErrorState error={error} />}

      {/* No accounts */}
      {awsAccounts.length === 0 && !loading && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>Connect an AWS account to view cost per resource.</Alert>
      )}

      {/* Data loaded */}
      {data && !loading && !error && (
        <>
          {/* Summary bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
            <Paper elevation={0} sx={{ px: 2, py: 1, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>Period</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{data.period?.start} — {data.period?.end}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>Total Cost</Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{fmt(data.grandTotal)}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>Services</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{data.serviceCount}</Typography>
              </Box>
              {data.cached && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Chip label="Cached" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                </>
              )}
            </Paper>
          </Box>

          {/* Charts */}
          {data.services?.length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <Card elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>Cost by Service</Typography>
                <ServicePieChart services={data.services} />
              </Card>
              <Card elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>Daily Spend Trend</Typography>
                <DailyTrendChart dailyCosts={data.dailyCosts} />
              </Card>
              <Card elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>Top 10 Resources</Typography>
                <TopResourcesChart topResources={data.topResources} />
              </Card>
              <Card elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 1 }}>Cost by Region</Typography>
                <RegionDistributionChart regionDistribution={data.regionDistribution} />
              </Card>
            </Box>
          )}

          {/* Search bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField size="small" placeholder="Search services..."
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Box sx={{ mr: 1, display: 'flex', color: 'text.disabled' }}><SearchIcon /></Box> }}
              sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} />
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="text" onClick={() => { const all = {}; (data.services || []).forEach(s => { all[s.service] = true; }); setExpanded(all); }}
              sx={{ fontSize: '0.75rem', textTransform: 'none', color: 'text.secondary' }}>Expand All</Button>
            <Button size="small" variant="text" onClick={() => setExpanded({})}
              sx={{ fontSize: '0.75rem', textTransform: 'none', color: 'text.secondary' }}>Collapse All</Button>
          </Box>

          {/* Empty search */}
          {filtered.length === 0 && search.trim() && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary">No services match "{search}"</Typography>
            </Box>
          )}

          {/* Empty data */}
          {data.services?.length === 0 && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No cost data available for the selected timeframe. This may happen if Cost Explorer was recently enabled or no resources were used during this period.
            </Alert>
          )}

          {/* Service list */}
          {filtered.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {filtered.map((svc, si) => {
                const isExpanded = expanded[svc.service];
                const svcPct = data.grandTotal > 0 ? (svc.totalCost / data.grandTotal) * 100 : 0;
                return (
                  <Card key={si} elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                    <Box onClick={() => toggleExpand(svc.service)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, cursor: 'pointer',
                        bgcolor: isExpanded ? alpha(theme.palette.text.primary, 0.02) : 'transparent',
                        '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.03) }, transition: 'background 0.15s'
                      }}>
                      <Box sx={{ color: 'text.secondary', display: 'flex' }}><ChevronIcon open={isExpanded} /></Box>
                      <Box sx={{ color: 'text.secondary', display: 'flex' }}>{getServiceIcon(svc.service)}</Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {svc.service}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {svc.percentage?.toFixed(1)}% of total
                        </Typography>
                      </Box>
                      <Box sx={{ width: 100, display: { xs: 'none', md: 'block' } }}>
                        <Box sx={{ height: 4, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.06) }}>
                          <Box sx={{ height: '100%', borderRadius: 2, width: `${Math.min(Math.abs(svcPct), 100)}%`, bgcolor: theme.palette.text.secondary, transition: 'width 0.4s ease' }} />
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, minWidth: 85, textAlign: 'right', color: svc.totalCost < 0 ? '#10B981' : 'text.primary' }}>
                        {fmt(svc.totalCost)}
                      </Typography>
                    </Box>
                    <Collapse in={isExpanded}>
                      <Divider />
                      <ServiceDrillDown
                        accountId={selectedAccount}
                        service={svc.service}
                        timeRange={timeRange}
                        customStartDate={customStartDate}
                        customEndDate={customEndDate}
                        theme={theme}
                      />
                    </Collapse>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}

      {!loading && !data && !error && awsAccounts.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body2" color="text.secondary">Select an AWS account to view cost per resource.</Typography>
        </Box>
      )}

      {/* Astra Chat FAB */}
      {selectedAccount && !chatOpen && (
        <Tooltip title="Astra - Cost Assistant">
          <IconButton onClick={openChatGlobal}
            sx={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 1400,
              width: 52, height: 52, borderRadius: '16px',
              background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
              color: '#fff', boxShadow: '0 4px 14px rgba(124,58,237,0.4)',
              '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)' }
            }}>
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 4h12a2 2 0 012 2v6a2 2 0 01-2 2H8l-4 3v-3a2 2 0 01-2-2V6a2 2 0 012-2z" strokeLinejoin="round" />
              <circle cx="7" cy="9" r="0.5" fill="currentColor" />
              <circle cx="10" cy="9" r="0.5" fill="currentColor" />
              <circle cx="13" cy="9" r="0.5" fill="currentColor" />
            </svg>
          </IconButton>
        </Tooltip>
      )}

      {/* Astra Chat Drawer */}
      <Drawer
        anchor="right"
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        sx={{ zIndex: 1500 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            borderRadius: '16px 0 0 16px',
            boxShadow: '-8px 0 30px rgba(0,0,0,0.12)',
            display: 'flex', flexDirection: 'column'
          }
        }}
      >
        {/* Chat Header */}
        <Box sx={{
          p: 2.5, borderBottom: '1px solid', borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(37,99,235,0.06) 100%)'
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 1.5,
                background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5">
                  <path d="M10 2v16" strokeLinecap="round" />
                  <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Astra</Typography>
                <Typography variant="caption" color="text.secondary">
                  {chatServiceContext ? chatServiceContext.service : 'Cost Per Resource Assistant'}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setChatOpen(false)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
              </svg>
            </IconButton>
          </Box>
          {chatServiceContext && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.12)' }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip label={chatServiceContext.service?.substring(0, 30)} size="small"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha('#2563EB', 0.1), color: '#2563EB' }} />
                <Box sx={{ ml: 'auto' }}>
                  <Button size="small" onClick={() => { setChatServiceContext(null); setChatMessages([]); }}
                    sx={{ fontSize: '0.65rem', textTransform: 'none', minWidth: 0, px: 1, color: 'text.secondary' }}>
                    Switch to global
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Chat Messages */}
        <Box sx={{
          flex: 1, overflow: 'auto', p: 2.5,
          display: 'flex', flexDirection: 'column', gap: 2,
          bgcolor: alpha(theme.palette.grey[500], 0.02)
        }}>
          {chatMessages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
                background: 'linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(37,99,235,0.1) 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="24" height="24" viewBox="0 0 20 20" fill="none" stroke="#7c3aed" strokeWidth="1.5">
                  <path d="M10 2v16" strokeLinecap="round" />
                  <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {chatServiceContext ? `Ask me about "${chatServiceContext.service}"` : 'Hi, I\'m Astra. How can I help with your AWS costs?'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                {chatServiceContext
                  ? 'I have context of this service\'s usage types, components, and cost breakdown.'
                  : 'I can explain usage types, analyze spend patterns, and suggest optimizations.'}
              </Typography>
              <Stack spacing={1}>
                {(chatServiceContext ? [
                  `What are the main cost drivers for ${chatServiceContext.service?.replace(/Amazon |AWS /g, '')}?`,
                  'Explain the usage types listed here',
                  'How can I reduce costs for this service?',
                  'Are there Reserved Instance options available?'
                ] : [
                  'What are my top spending services?',
                  'Explain what "APS3-DataTransfer-In-Bytes" means',
                  'Which services are costing the most?',
                  'How can I reduce my overall AWS spend?',
                  'What does "USW2-BoxUsage:t3.medium" mean?'
                ]).map((suggestion, idx) => (
                  <Box key={idx} onClick={() => setChatInput(suggestion)}
                    sx={{
                      p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                      '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(124,58,237,0.03)' }
                    }}>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>{suggestion}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {chatMessages.map((msg, idx) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 1 }}>
              {msg.role === 'assistant' && (
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0, mt: 0.5,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5">
                    <path d="M10 2v16" strokeLinecap="round" />
                    <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Box>
              )}
              <Box sx={{
                maxWidth: '85%', p: 2, borderRadius: 2.5,
                ...(msg.role === 'user' ? {
                  bgcolor: 'primary.main', color: 'white', borderBottomRightRadius: 4
                } : {
                  bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderBottomLeftRadius: 4
                })
              }}>
                {msg.role === 'user' ? (
                  <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                ) : (
                  <Box sx={{
                    '& p': { m: 0, mb: 1, lineHeight: 1.7, fontSize: '0.875rem', '&:last-child': { mb: 0 } },
                    '& ul, & ol': { m: 0, mb: 1, pl: 2.5, '&:last-child': { mb: 0 } },
                    '& li': { mb: 0.5, fontSize: '0.875rem', lineHeight: 1.6 },
                    '& strong': { fontWeight: 600 },
                    '& code': { bgcolor: 'grey.100', color: 'text.primary', px: 0.75, py: 0.25, borderRadius: 0.5, fontSize: '0.8rem', fontFamily: 'monospace' },
                    '& pre': { bgcolor: '#1e1e1e', color: '#e0e0e0', p: 1.5, borderRadius: 1.5, overflow: 'auto', mb: 1, '& code': { bgcolor: 'transparent', color: 'inherit', p: 0, fontSize: '0.78rem' } },
                    '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.5, fontWeight: 700, fontSize: '0.9rem' },
                    '& table': { borderCollapse: 'collapse', width: '100%', mb: 1, fontSize: '0.8rem' },
                    '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
                    '& th': { bgcolor: 'grey.50', fontWeight: 600 }
                  }}>
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </Box>
                )}
              </Box>
            </Box>
          ))}

          {isChatLoading && (
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="1.5">
                  <path d="M10 2v16" strokeLinecap="round" />
                  <path d="M14 6c0-1.1-1.8-2-4-2S6 4.9 6 6s1.8 2 4 2 4 .9 4 2-1.8 2-4 2-4-.9-4-2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Box>
              <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" gap={0.5}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{
                      width: 6, height: 6, borderRadius: '50%', bgcolor: '#7c3aed',
                      animation: 'astraPulse 1.4s infinite', animationDelay: `${i * 0.2}s`,
                      '@keyframes astraPulse': {
                        '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                        '40%': { opacity: 1, transform: 'scale(1)' }
                      }
                    }} />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
          <div ref={chatEndRef} />
        </Box>

        {/* Chat Input */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box display="flex" gap={1}>
            <TextField fullWidth size="small"
              placeholder={chatServiceContext ? `Ask Astra about ${chatServiceContext.service?.replace(/Amazon |AWS /g, '')}...` : 'Ask Astra about your AWS costs...'}
              value={chatInput} onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
              disabled={isChatLoading} multiline maxRows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontSize: '0.875rem' } }}
            />
            <IconButton onClick={handleChatSend} disabled={!chatInput.trim() || isChatLoading}
              sx={{
                width: 40, height: 40, borderRadius: 2,
                background: chatInput.trim() ? 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)' : 'transparent',
                color: chatInput.trim() ? 'white' : 'text.disabled',
                '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)' },
                '&.Mui-disabled': { background: 'transparent' }
              }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default ResourceBreakdown;
